import { FormatDateOptions, IntlShape } from 'react-intl'

import { AxiosInstance } from 'axios'

import {
  ESSENTIALS_CMS_QUERY_APP_ENV,
  EssentialsEventData,
  EssentialsEventDataApiResponse,
  EssentialsUpcomingEventData,
  ZOOM_EVENT_TYPE,
} from './types'
import { CDN_ASSETS_LINK_BASE, IS_PRODUCTION } from '../common/constants/constants'
import { ESSENTIALS_LIVE_EVENTS_PROPERTIES } from '../common/constants/mixpanelConstants'
import { postUserInteraction, UserInteractionType } from '../userInteraction/data'

const CONTENT_KIND_TO_INTERACTION_TYPE = {
  ARTICLE: UserInteractionType.ARTICLE_CLICKED,
  COURSE_LESSON: UserInteractionType.COURSE_COMPLETED,
  MEDITATION: UserInteractionType.AUDIO_COMPLETED,
} as const

const isSupportedContentKind = (contentKind?: string): contentKind is keyof typeof CONTENT_KIND_TO_INTERACTION_TYPE =>
  contentKind != null && Object.prototype.hasOwnProperty.call(CONTENT_KIND_TO_INTERACTION_TYPE, contentKind)

/**
 * Sends supported Essentials interactions to LWAPI without interrupting the current Essentials flow.
 * HTTP failures are logged by the configured Axios interceptor.
 */
export const submitUserInteraction = (
  httpClient: Pick<AxiosInstance, 'post'>,
  message: {
    event?: string
    contentId?: string
    contentKind?: string
    completionPercent?: number
    contentViewId?: string
  },
): void => {
  const contentViewId = typeof message.contentViewId === 'string' ? message.contentViewId.trim() : ''
  if (
    message.event !== 'USER_REACHED_CONTENT_THRESHOLD' ||
    !isSupportedContentKind(message.contentKind) ||
    contentViewId === ''
  ) {
    return
  }

  void postUserInteraction(httpClient, {
    id: contentViewId,
    interaction_type: CONTENT_KIND_TO_INTERACTION_TYPE[message.contentKind],
    interaction_date: new Date().toISOString().slice(0, 10),
  }).catch(() => undefined)
}

// @todo clean up essentialsscreen/essentials
export const getEssentialsEventZoomInfo = async ({
  axiosInstance,
  lyraId,
  events,
}: {
  axiosInstance: AxiosInstance
  lyraId: string
  events: { zoom_event_id: string; zoom_event_type: string }[]
}): Promise<
  { current_number_of_registrants: number; zoom_event_id: string; zoom_event_type: ZOOM_EVENT_TYPE }[] | null
> => {
  const requestData = {
    lyra_id: lyraId,
    events,
  }
  let response
  try {
    response = await axiosInstance.post(`/v1/zoom-event/get-zoom-event-infos`, requestData)
  } catch (e) {
    console.error('Failed to fetch Zoom event info:', e)
    return null
  }
  return response?.data?.events
}

/** Format the essentials getEvents api response into EssentialsEventData and sort by date */
export const formatEssentialsEventData = (eventsList: EssentialsEventDataApiResponse[]) => {
  let finalList: EssentialsEventData[] = eventsList.map((eventItem) => {
    const newEvent = {
      ...eventItem,
      eventTimes: eventItem.eventTimes.map((time) => ({
        ...time,
        eventDate: new Date(time.eventDate),
      })),
      eventSeriesTimes: eventItem.eventSeriesTimes.map((time) => ({
        ...time,
        eventDate: new Date(time.eventDate),
      })),
      facilitators: eventItem.facilitators.map((facilitator) => ({
        ...facilitator,
        mainPhoto: '',
      })),
    }

    newEvent.eventTimes = newEvent.eventTimes.filter((eventTime) => eventTime.eventDate > new Date())
    newEvent.eventSeriesTimes = newEvent.eventSeriesTimes.filter((eventTime) => eventTime.eventDate > new Date())

    if (newEvent.eventSeries) {
      newEvent.eventTimes = newEvent.eventSeriesTimes
    }

    newEvent.facilitators.forEach((facilitator) => {
      const facilitatorPhoto = facilitator.photo[0]
      if (facilitatorPhoto?.url) {
        facilitatorPhoto.url = `https:${facilitatorPhoto.url}`
        facilitator.mainPhoto = facilitatorPhoto.url
      } else {
        facilitator.mainPhoto = `${CDN_ASSETS_LINK_BASE}/abstract_face.png`
      }
    })
    return newEvent
  })

  finalList = finalList.filter((event) => event.eventTimes.length > 0)

  finalList.sort((a, b) => {
    const aTime = a.eventTimes[0]?.eventDate?.getTime() ?? Infinity
    const bTime = b.eventTimes[0]?.eventDate?.getTime() ?? Infinity
    return aTime - bTime
  })
  return finalList
}

export const getEventDateString = (event: EssentialsEventData, intl: IntlShape): string => {
  if (!event.eventTimes.length) return ''
  const withDayFormat: FormatDateOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }
  const withoutDayFormat: FormatDateOptions = {
    month: 'short',
    day: 'numeric',
  }
  let firstDate: string = ''
  let seriesDates: string[] = []
  if (event.eventSeries) {
    seriesDates = event.eventSeriesTimes.map((eventTime, index) => {
      if (index === 0) {
        return intl.formatDate(eventTime.eventDate, withDayFormat)
      } else {
        return intl.formatDate(eventTime.eventDate, withoutDayFormat)
      }
    })
  } else {
    firstDate = intl.formatDate(event.eventTimes[0].eventDate, withDayFormat)
  }

  if (seriesDates.length) {
    return seriesDates.join(', ')
  } else if (event.eventTimes.length > 1) {
    return intl.formatMessage(
      {
        defaultMessage: '{date} + More',
        description: 'Event date with indication of additional dates',
      },
      { date: firstDate },
    )
  } else {
    return firstDate
  }
}

export const getAnalyticsPropsForEvent = (event: EssentialsUpcomingEventData, index: number) => {
  return {
    [ESSENTIALS_LIVE_EVENTS_PROPERTIES.EVENT_ID]: event.id,
    [ESSENTIALS_LIVE_EVENTS_PROPERTIES.EVENT_TIMES]: event.eventTimes.map((time) => time.eventDate.toISOString()),
    [ESSENTIALS_LIVE_EVENTS_PROPERTIES.EVENT_FACILITATORS]: event.facilitators.map(
      (facilitator) => facilitator.facilitatorName,
    ),
    [ESSENTIALS_LIVE_EVENTS_PROPERTIES.EVENTS_SHOWN_RANK]: index + 1,
    [ESSENTIALS_LIVE_EVENTS_PROPERTIES.DESTINATION_URL]: event.uri,
  }
}

export const getEssentialsAppEnv = () => {
  return !IS_PRODUCTION ? ESSENTIALS_CMS_QUERY_APP_ENV.DEV : ESSENTIALS_CMS_QUERY_APP_ENV.PROD
}
